/**
 * Fixed bottom navigation bar with three tabs: Scan, Boxes, Search.
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  path: string;
  match: (pathname: string) => boolean;
  icon: (active: boolean) => ReactNode;
}

const items: NavItem[] = [
  {
    label: 'Scan',
    path: '/',
    match: (p) => p === '/',
    icon: (active) => (
      <svg
        className={`h-6 w-6 ${active ? 'text-accent' : 'text-text-secondary'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={active ? 2.5 : 2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
        />
      </svg>
    ),
  },
  {
    label: 'Boxes',
    path: '/boxes',
    match: (p) => p.startsWith('/boxes'),
    icon: (active) => (
      <svg
        className={`h-6 w-6 ${active ? 'text-accent' : 'text-text-secondary'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={active ? 2.5 : 2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
        />
      </svg>
    ),
  },
  {
    label: 'Search',
    path: '/search',
    match: (p) => p.startsWith('/search'),
    icon: (active) => (
      <svg
        className={`h-6 w-6 ${active ? 'text-accent' : 'text-text-secondary'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={active ? 2.5 : 2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    ),
  },
];

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]"
      aria-label="Main navigation"
    >
      <div className="mx-auto flex max-w-screen-lg items-center justify-around h-16">
        {items.map((item) => {
          const active = item.match(location.pathname);
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 min-w-[64px] transition-colors"
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              {item.icon(active)}
              <span
                className={`text-[10px] font-medium ${active ? 'text-accent' : 'text-text-secondary'}`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
